// PDP block "Gratis-Stufen (Custom)". Display only: mirrors the cart drawer's gift
// rules (ds-cart-model.js) and never writes to the cart. Liquid renders the start
// state; this element re-renders it on every ds-cart:render.
import {
  automaticGifts, campaignFor, cents, giftChoices, qualifyingAmount, selectedGifts, stepEligible
} from "ds-cart-model";

const GIFT_TYPES = ["automatic_gift", "selectable_gifts"];

export function giftTierDiscounts(campaign, tiers, formatMoney, template, offer) {
  const useOffer = Boolean(offer?.campaignId && offer.campaignId === campaign?.id);
  const code = String((useOffer ? offer.code : campaign?.code) || "").trim();
  return {
    codes: code ? [code] : [],
    texts: tiers.map(({ step }) => {
      const amount = useOffer ? offer.amounts?.[String(Number(step.free_from_amount))] : step.extra_discount;
      return code && cents(amount) > 0
        ? String(template || "").replace("[amount]", formatMoney(cents(amount))) : "";
    })
  };
}

// Pure state used by the element and the node tests.
export function giftTiersState(cart, campaign, lines = {}) {
  const steps = (campaign?.steps || []).filter((step) =>
    step.show_in_progressbar && GIFT_TYPES.includes(step.type)
  );
  const merchandise = qualifyingAmount(cart, steps[0] || {}, lines);
  let total = 0, unlocked = 0, next = null;
  const tiers = steps.map((step, index) => {
    const selectable = step.type === "selectable_gifts";
    const reached = stepEligible(step, cart, lines);
    const choices = selectable ? giftChoices(step) : automaticGifts(step);
    const limit = Math.max(1, Number(step.gift_selection_limit || 1));
    const count = selectable ? Math.min(limit, choices.length) : choices.length;
    const chosen = new Set(selectedGifts(cart, step).map((item) => String(item.variant_id)));
    const amount = cents(step.free_from_amount);
    total += count;
    if (reached) unlocked += count;
    else if (!next) next = { step, amount, remaining: Math.max(0, amount - merchandise) };
    const cards = choices.map((choice) => {
      let state = "reached";
      if (!reached) state = "locked";
      else if (selectable && !chosen.has(String(choice.variant_id))) {
        state = chosen.size >= limit ? "muted" : "selectable";
      }
      return { key: `${step.id}:${choice.variant_id}`, step: String(step.id), state, choice };
    });
    return { step, index, amount, reached, count, cumulative: total, cards };
  });
  return {
    merchandise, tiers, total, unlocked, next,
    remaining: next ? next.remaining : 0
  };
}

const formatter = (locale, currency, trim) => new Intl.NumberFormat(locale, {
  style: "currency", currency, ...(trim ? { minimumFractionDigits: 0 } : {})
});

// The fallback base class lets node tests import giftTiersState.
class CustomGiftTiers extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    try {
      this.settings = JSON.parse(this.querySelector("[data-cgt-config]")?.textContent || "{}");
    } catch {
      this.settings = {};
    }
    // Sample cards without sample products have nothing to recompute.
    if (this.settings.preview && !this.settings.previewCampaign) return;
    this.onRender = () => this.schedule();
    this.connect();
  }

  disconnectedCallback() {
    if (this.onRender) document.removeEventListener("ds-cart:render", this.onRender);
    cancelAnimationFrame(this.frame);
  }

  async connect() {
    try {
      const { cartAdapter, readConfig } = await import("ds-cart-ajax");
      this.adapter = cartAdapter;
      this.readConfig = readConfig;
      document.addEventListener("ds-cart:render", this.onRender);
      await cartAdapter.ready;
      this.schedule();
    } catch (error) {
      // Keep the Liquid start state when the DS cart is unavailable.
      console.error("custom-gift-tiers:", error);
    }
  }

  schedule() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.render());
  }

  render() {
    const cart = this.adapter?.cart;
    const config = this.readConfig?.() || {};
    const sample = this.settings.previewCampaign;
    const campaigns = Array.isArray(config.campaigns) ? config.campaigns : [];
    // Without the drawer configuration the server-rendered state is the best we have.
    if (!cart) return;
    if (!sample && !campaigns.length) {
      if (this.settings.onlyCampaign || this.settings.showCampaignDiscounts) this.hidden = true;
      return;
    }
    const override = this.settings.campaignOverride;
    // Preview: sample tiers measured against the real cart.
    const campaign = sample ||
      (override && campaigns.find((entry) => String(entry.id) === override)) ||
      (!this.settings.onlyCampaign && campaignFor(cart, campaigns));
    if (campaign?.excluded_product_ids?.map(String).includes(String(this.settings.productId))) {
      this.hidden = true;
      return;
    }
    const state = giftTiersState(cart, campaign, config.lines || {});
    this.hidden = state.tiers.length === 0;
    if (this.hidden) return;

    const currency = cart.currency || config.currency || "EUR";
    const locale = config.locale || "de";
    const money = formatter(locale, currency, false);
    const short = formatter(locale, currency, true);
    this.formatMoney = (value) => money.format(value / 100);
    this.formatShort = (value) => short.format(value / 100);

    if (campaign?.is_global_gift_campaign || this.settings.showCampaignDiscounts) {
      const discounts = giftTierDiscounts(campaign, state.tiers, this.formatShort, this.settings.discountTemplate, this.settings.discountOffer);
      this.settings.discountCodes = discounts.codes;
      this.settings.discountTexts = discounts.texts;
    }

    this.renderStatus(state);
    this.renderCards(state);
    this.renderTiers(state);
  }

  renderStatus(state) {
    const s = this.settings;
    const line = this.querySelector("[data-cgt-statusline]");
    if (!line) return;
    const progress = String(s.statusProgress || "")
      .replace("[unlocked]", state.unlocked).replace("[total]", state.total);
    let next = s.statusAll || "";
    if (state.next) {
      next = state.remaining > 0
        ? String(s.statusNext || "").replace("[amount]", this.formatMoney(state.remaining))
        : s.statusCondition || "";
    }
    const text = [progress, next].filter(Boolean).join(" · ");
    if (line.textContent !== text) line.textContent = text;
  }

  renderCards(state) {
    const list = this.querySelector("[data-cgt-cards]");
    const template = this.querySelector("[data-cgt-card-template]");
    if (!list || !template) return;
    const existing = new Map([...list.children].map((node) => [node.dataset.key, node]));
    let position = 0;
    const nodes = state.tiers.flatMap((tier) => tier.cards.map((card) => {
      position += 1;
      const node = existing.get(card.key) || template.content.firstElementChild.cloneNode(true);
      this.fillCard(node, card, tier, position);
      return node;
    }));
    // Only touch the DOM order when it changed, so images do not reload.
    const same = nodes.length === list.children.length && nodes.every((node, index) => list.children[index] === node);
    if (!same) list.replaceChildren(...nodes);
  }

  fillCard(node, card, tier, position) {
    const s = this.settings;
    const { choice } = card;
    const locked = card.state === "locked";
    node.dataset.key = card.key;
    node.dataset.step = card.step;
    node.dataset.state = card.state;

    const image = node.querySelector("[data-cgt-image]");
    const src = s.images?.[position - 1] || choice.featured_image || "";
    if (image) {
      if (src && image.getAttribute("src") !== src) image.setAttribute("src", src);
      image.hidden = !src;
    }

    const variantTitle = choice.variant_title && choice.variant_title !== "Default Title" ? ` – ${choice.variant_title}` : "";
    const title = locked && !s.showRealName ? s.lockedTitle : `${choice.title}${variantTitle}`;
    const badge = locked ? String(s.badgeLocked || "").replace("[amount]", this.formatShort(tier.amount)) : s.badgeReached;
    const status = { reached: s.statusReached, selectable: s.statusSelectable, locked: s.statusLocked }[card.state] || "";
    const discount = s.discountTexts?.[tier.index] || "";
    const chip = s.showChip && discount ? String(s.chipText || "").replace("[discount]", discount) : "";
    const codes = s.discountCodes || [];
    const code = chip ? codes[tier.index] || codes[codes.length - 1] || "" : "";
    const chipCode = code ? String(s.codeText || "").replace("[code]", code) : "";

    this.setText(node, "[data-cgt-title]", title);
    this.setText(node, "[data-cgt-badge]", badge);
    this.setText(node, "[data-cgt-value]", `${s.valuePrefix || ""}${this.formatMoney(cents(choice.price))}`);
    this.setText(node, "[data-cgt-status]", status);
    const chipNode = node.querySelector("[data-cgt-chip]");
    if (chipNode) {
      this.setText(chipNode, "[data-cgt-chip-text]", chip);
      this.setText(chipNode, "[data-cgt-chip-code]", chipCode);
      const codeNode = chipNode.querySelector("[data-cgt-chip-code]");
      if (codeNode) codeNode.hidden = !chipCode;
      chipNode.hidden = !chip;
      chipNode.classList.toggle("is-reached", tier.reached);
    }
  }

  renderTiers(state) {
    const s = this.settings;
    const list = this.querySelector("[data-cgt-tiers]");
    if (!list) return;
    list.hidden = !s.showTierList;
    const rows = state.tiers.map((tier) => {
      const count = s.cumulativeCount ? tier.cumulative : tier.count;
      const word = count === 1 ? s.giftWordOne : s.giftWordMany;
      const discount = s.discountTexts?.[tier.index] || "";
      const codes = s.discountCodes || [];
      const code = codes[tier.index] || codes[codes.length - 1] || "";
      const text = [
        String(s.tierRowPrefix || "").replace("[amount]", this.formatShort(tier.amount)),
        `${count} ${word || ""}`.trim(),
        discount && code ? `+ ${discount} ${String(s.codeText || "").replace("[code]", code)}` : ""
      ].filter(Boolean).join(" ");
      return { text, reached: tier.reached, next: state.next?.step.id === tier.step.id };
    });
    const current = [...list.children];
    rows.forEach((row, index) => {
      let node = current[index];
      if (!node) {
        node = document.createElement("li");
        node.className = "cgt__tier";
        const check = document.createElement("span");
        check.className = "cgt__check";
        check.setAttribute("aria-hidden", "true");
        node.append(check, document.createElement("span"));
        list.append(node);
      }
      node.dataset.reached = String(row.reached);
      node.dataset.next = String(row.next);
      const label = node.lastElementChild;
      if (label.textContent !== row.text) label.textContent = row.text;
    });
    current.slice(rows.length).forEach((node) => node.remove());
  }

  setText(root, selector, text) {
    const node = root.querySelector(selector);
    if (node && node.textContent !== (text || "")) node.textContent = text || "";
  }
}

if (typeof customElements !== "undefined" && !customElements.get("custom-gift-tiers")) {
  customElements.define("custom-gift-tiers", CustomGiftTiers);
}
